import { tr } from '../i18n';
import type { ModelGroup, ModelInfo, ModelsResponse } from '../service/orchestrator-client';
import type { Conversation } from '../types';

import type { ModelThinking } from './thinking-levels';

/**
 * The absence of a choice: the model id a new chat carries until the user picks
 * one. It is never sent, so the orchestrator runs the last model a request named
 * on the session, else its default. The control shows that default by name
 * (`GET /api/v1/models` → `default`) unless the chat's session was started
 * elsewhere. There is no "Default" entry: every model and label comes from
 * `/models`, and the FE owns none.
 */
export const DEFAULT_MODEL_ID = 'default';

/** True when `id` is the "no choice" sentinel (or empty). */
export function isDefaultModel(id: string | undefined): boolean {
  return !id || id === DEFAULT_MODEL_ID;
}

export interface ModelOption {
  id: string;
  label: string;
  description: string;
  /** Its mark's id (`components/ModelMark.tsx`); absent where the server sends none. */
  icon?: string;
  /** The group it is listed under. */
  group: ModelGroup;
  /**
   * Its family's levels; null where the server gives none: an older orchestrator, or a model
   * that takes no level, whose slider is not drawn.
   */
  thinking: ModelThinking | null;
}

/** The models of one group, in the server's order. */
export interface ModelOptionGroup extends ModelGroup {
  options: ModelOption[];
}

/** What the composer knows of the chat's session, besides the model it picked. */
export interface SessionView {
  /** The session may run a model picked elsewhere (see isSessionModelUnknown). */
  sessionModelUnknown?: boolean;
  /** The session read a connected account: it runs the deployment's model whatever is picked. */
  connected?: boolean;
}

export interface ModelPicker {
  /** The `investbanq` group, under "In-house". */
  inHouse: ModelOptionGroup[];
  /** Every other group, in the server's order, under "Third-party". */
  thirdParty: ModelOptionGroup[];
  /**
   * The model the chip names and whose levels the slider draws (see shownModel). Null when
   * none is shown.
   */
  shown: ModelOption | null;
  /** A partner override: the panes hold the partner's model alone, disabled. */
  override: boolean;
  /** A connected session: the panes are hidden. */
  connected: boolean;
}

/** The group id shown under "In-house". */
export const IN_HOUSE_GROUP = 'investbanq';

/**
 * Each provider's group, for an older orchestrator that sends no `group` (the current one
 * groups by provider itself: investbanq AI, then Anthropic, Google, OpenAI, xAI).
 */
const PROVIDER_GROUPS: Record<string, ModelGroup> = {
  investbanq: { id: 'investbanq', label: 'investbanq AI' },
  anthropic: { id: 'anthropic', label: 'Anthropic' },
  openai: { id: 'openai', label: 'OpenAI' },
  azure_openai: { id: 'openai', label: 'OpenAI' },
  gemini: { id: 'google', label: 'Google' },
  grok: { id: 'xai', label: 'xAI' },
};

/** The group of a model whose provider names none. */
function otherGroup(): ModelGroup {
  return { id: 'other', label: tr('picker.otherGroup', 'Other') };
}

/** The group a model is listed under: the server's, else its provider's, else Other. */
export function modelGroupOf(model: Pick<ModelInfo, 'group' | 'provider'>): ModelGroup {
  return (
    model.group ?? (model.provider ? PROVIDER_GROUPS[model.provider] : undefined) ?? otherGroup()
  );
}

function optionOf(model: ModelInfo): ModelOption {
  return {
    id: model.id,
    // Labels fall back to the id for backends that don't send display copy.
    label: model.label ?? model.id,
    description: model.description ?? '',
    icon: model.icon,
    group: modelGroupOf(model),
    thinking: model.thinking ?? null,
  };
}

/**
 * The deployment's default as an option when the list does not hold it: `default_model`, or by
 * its id under Other from a server without `default_model`. Undefined when the list holds it or
 * the server names none.
 */
function unlistedDefault(res: ModelsResponse): ModelOption | undefined {
  const dflt = res.default;
  if (!dflt || res.models.some((m) => m.id === dflt)) return undefined;
  if (res.default_model) return optionOf(res.default_model);
  return { id: dflt, label: dflt, description: '', group: otherGroup(), thinking: null };
}

/** The deployment's default: listed, else `default_model`; null when the server names none. */
function defaultOption(res: ModelsResponse): ModelOption | null {
  const listed = res.default ? res.models.find((m) => m.id === res.default) : undefined;
  return listed ? optionOf(listed) : (unlistedDefault(res) ?? null);
}

/**
 * The model a request runs on as this client can know it: `override_model` under a partner
 * override; `default_model` in a connected session, which runs the deployment's model; else the
 * chat's model when the list holds it; else none for a chat with no choice whose session may
 * hold a model picked elsewhere (the orchestrator keeps the last model a request named on a
 * session and never reports it); else the server's default. Null when none is shown.
 */
export function shownModel(
  res: ModelsResponse,
  modelId: string,
  session: SessionView = {},
): ModelOption | null {
  if (res.override_active) return res.override_model ? optionOf(res.override_model) : null;
  if (session.connected) {
    return res.default_model ? optionOf(res.default_model) : defaultOption(res);
  }
  const listed = isDefaultModel(modelId) ? undefined : res.models.find((m) => m.id === modelId);
  if (listed) return optionOf(listed);
  if (session.sessionModelUnknown && isDefaultModel(modelId)) return null;
  return defaultOption(res);
}

/**
 * The control's view of `/models` for a chat whose model is `modelId`: the groups under In-house
 * and Third-party, in the server's order, and the shown model. A default the server does not
 * list shows first under its group; picking it stores an id selectableModel never sends, so the
 * turn stays on that default.
 */
export function modelPicker(
  res: ModelsResponse,
  modelId: string,
  session: SessionView = {},
): ModelPicker {
  const shown = shownModel(res, modelId, session);
  if (res.override_active) {
    // A partner's provider is never investbanq, and its model need not be listed.
    const groups = shown ? [{ ...shown.group, options: [shown] }] : [];
    return { inHouse: [], thirdParty: groups, shown, override: true, connected: false };
  }
  if (session.connected) {
    return { inHouse: [], thirdParty: [], shown, override: false, connected: true };
  }
  const groups: ModelOptionGroup[] = [];
  const add = (option: ModelOption, first: boolean) => {
    let group = groups.find((g) => g.id === option.group.id);
    if (!group) {
      group = { ...option.group, options: [] };
      groups.push(group);
    }
    if (first) group.options.unshift(option);
    else group.options.push(option);
  };
  for (const m of res.models) add(optionOf(m), false);
  const dflt = unlistedDefault(res);
  if (dflt) add(dflt, true);
  return {
    inHouse: groups.filter((g) => g.id === IN_HOUSE_GROUP),
    thirdParty: groups.filter((g) => g.id !== IN_HOUSE_GROUP),
    shown,
    override: false,
    connected: false,
  };
}

/** A picked model's label and provider group, kept on the chat beside its id. */
export interface ModelName {
  label: string;
  groupId: string;
}

/**
 * The chat's own model, which a successful `/models` read no longer lists (DEV-4575 C-F7): the
 * server omits a model it cannot run, and marks none. Never under a partner override or in a
 * connected session (neither runs the chat's model), for the "no choice" sentinel, for the
 * unlisted default modelPicker offers, or after a failed read (EMPTY_MODELS, which has no
 * `default`).
 */
export function isModelGone(
  res: ModelsResponse,
  modelId: string,
  session: SessionView = {},
): boolean {
  return (
    !isDefaultModel(modelId) &&
    res.default !== undefined &&
    !res.override_active &&
    !session.connected &&
    modelId !== res.default &&
    !res.models.some((m) => m.id === modelId)
  );
}

/**
 * True for a chat bound to a session it did not start (a history stub, or a chat
 * persisted without `sessionStartedHere`): that session may run a model picked
 * elsewhere (see shownModel).
 */
export function isSessionModelUnknown(
  conversation: Pick<Conversation, 'sessionId' | 'sessionStartedHere'>,
): boolean {
  return conversation.sessionId !== undefined && conversation.sessionStartedHere !== true;
}
